import React, { useMemo } from "react";
import { Box, Typography, Paper, Button, useTheme } from "@mui/material";
import { WarningAmber as WarningAmberIcon } from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";

interface OrganisationSetupBannerProps {
  /** Called when user clicks "Complete Setup". Omit to hide the button (e.g. on Profile page). */
  onCompleteSetup?: () => void;
  sx?: Record<string, unknown>;
}

const OrganisationSetupBanner: React.FC<OrganisationSetupBannerProps> = ({
  onCompleteSetup,
  sx,
}) => {
  const theme = useTheme();
  const { user } = useAuth();

  const missingProfileFields = useMemo(() => {
    if (user?.userType === "team" || !user?.isSchoolApproved) return [];
    const missing: string[] = [];
    if (!user?.schoolLogo || user.schoolLogo === "" || user.schoolLogo === "/placeholder.jpg") {
      missing.push("logo");
    }
    if (!user?.orgBanner || user.orgBanner === "") {
      missing.push("banner");
    }
    if (!user?.phoneNumber || user.phoneNumber === "") {
      missing.push("phone");
    }
    return missing;
  }, [user]);

  if (missingProfileFields.length === 0) return null;

  const percentComplete = Math.round(((3 - missingProfileFields.length) / 3) * 100);

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        bgcolor: theme.palette.cards?.orangeBg || theme.palette.warning.light,
        border: "1px solid",
        borderColor: theme.palette.warning.light,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        flexDirection: { xs: "column", sm: "row" },
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flex: 1 }}>
        <WarningAmberIcon
          sx={{
            color: theme.palette.warning.main,
            fontSize: 28,
            mt: 0.25,
            flexShrink: 0,
          }}
        />
        <Box>
          <Typography
            sx={{
              fontWeight: 600,
              color: "text.primary",
              fontSize: "1rem",
            }}
          >
            Complete{" "}
            <Typography component="span" sx={{ fontWeight: 400, fontSize: "1rem" }}>
              Your Organisation Setup
            </Typography>
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.25 }}
          >
            Your organisation profile is not fully set up yet.
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary" }}
          >
            Finish setup to unlock full communication features.
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
        {onCompleteSetup && (
          <Button
            variant="contained"
            onClick={onCompleteSetup}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              py: 1,
              borderRadius: 2,
              bgcolor: theme.palette.warning.main,
              color: theme.palette.common.white,
              "&:hover": {
                bgcolor: theme.palette.warning.dark,
              },
            }}
          >
            Complete Setup
          </Button>
        )}
        <Typography
          variant="caption"
          sx={{ color: "text.secondary" }}
        >
          {percentComplete}% complete
        </Typography>
      </Box>
    </Paper>
  );
};

export default OrganisationSetupBanner;
